import {
  Anchor,
  Content,
  type PopoverContentProps,
  Portal,
  Root,
} from "@radix-ui/react-popover"
import type { ReactNode } from "react"

import { theme } from "../../theme"
import styles from "./Popover.module.css"

export interface PopoverProps {
  anchor: ReactNode
  children?: ReactNode
  focusOnEnter?: boolean
  open?: boolean
  openDelay?: number
  closeDelay?: number
  align?: "start" | "center" | "end"
  sideOffset?: number
  onOpenChange?: (open: boolean) => void
  onInteractOutside?: (event: PopoverInteractOutsideEvent) => void
  onEscapeKeyDown?: (event: KeyboardEvent) => void
}

export type PopoverInteractOutsideEvent = Parameters<
  NonNullable<PopoverContentProps["onInteractOutside"]>
>[0]

export function Popover(props: PopoverProps) {
  const {
    align,
    focusOnEnter = true,
    sideOffset,
    anchor,
    children,
    open,
    openDelay = 0,
    closeDelay = 0,
    onOpenChange,
    onInteractOutside,
    onEscapeKeyDown,
  } = props

  function onOpenAutoFocus(evt: Event) {
    if (!focusOnEnter) evt.preventDefault()
  }

  return (
    <Root open={open} onOpenChange={onOpenChange}>
      <Anchor asChild>{anchor}</Anchor>
      <Portal>
        <Content
          className={styles.cardContent}
          style={{
            "--Popover-open-delay": `${openDelay}ms`,
            "--Popover-close-delay": `${closeDelay}ms`,
          }}
          align={align}
          collisionPadding={parseFloat(theme.space[16])}
          sideOffset={sideOffset}
          onOpenAutoFocus={onOpenAutoFocus}
          onInteractOutside={onInteractOutside}
          onEscapeKeyDown={onEscapeKeyDown}
          /* Scroll event propagation is stopped to fix scroll inside dialogs
          https://github.com/prismicio/editor/pull/1164
          https://github.com/radix-ui/primitives/issues/1159 */
          onWheel={(event) => event.stopPropagation()}
        >
          {children}
        </Content>
      </Portal>
    </Root>
  )
}
